import React, { useCallback, useState, useEffect } from 'react';
import {
  useNodeId,
  useReactFlow,
  Handle,
  Position,
  NodeResizer,
} from '@xyflow/react';

const PreparationNode = ({ data, selected }) => {
  const id = useNodeId();
  const { setNodes } = useReactFlow();

  // local buffer so typing doesn’t churn the whole graph
  const [label, setLabel] = useState(data.label ?? '');
  const [desc, setDesc] = useState(data.description ?? '');

  // keep local state in sync if external changes happen (undo/redo, load, etc.)
  useEffect(() => setLabel(data.label ?? ''), [data.label]);
  useEffect(() => setDesc(data.description ?? ''), [data.description]);

  const handleResize = useCallback(
    (_evt, params) => {
      if (!id) return;
      const { width, height } = params || {};
      if (width == null && height == null) return;

      setNodes((ns) =>
        ns.map((n) =>
          n.id === id
            ? {
                ...n,
                style: {
                  ...(n.style || {}),
                  ...(width != null ? { width } : {}),
                  ...(height != null ? { height } : {}),
                },
                // also stash in data so your save routine (which only sends data) will persist it
                data: {
                  ...n.data,
                  ...(width != null ? { width } : {}),
                  ...(height != null ? { height } : {}),
                },
              }
            : n
        )
      );
    },
    [id, setNodes]
  );

  const commit = useCallback(
    (patch) => {
      if (!id) return;
      setNodes((ns) =>
        ns.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, ...patch } } : n
        )
      );
    },
    [id, setNodes]
  );

  const stopKeys = (e) => e.stopPropagation(); // prevent canvas hotkeys while typing

  return (
    <div
      className="preparation-node"
      style={{
        position: 'relative',
        background: 'transparent',
        width: data.width ?? 'auto',
        height: data.height ?? 'auto',
        '--data-color': data.bgColor ?? '#ffffff', 
      }}
    >
      <NodeResizer
        isVisible={selected}
        minWidth={200}
        minHeight={150}
        onResizeEnd={handleResize}
        onResize={handleResize}
      />

      <div
        className="grid align-items"
        style={{
          width: '60%',
          height: '100%',
          background: data.bgColor ?? '#ffffff',
          color: data.fontColor ?? '#111111',
          fontSize: (data.fontSize ?? 14) + 'px',
          padding: 12,
        }}
      >
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onBlur={() => commit({ label })}
          onKeyDown={stopKeys}
          className="node-label"
          style={{
            width: '100%',
            border: 'none',
            background: 'transparent',
            color: data.fontColor ?? '#111111',
            fontSize: (data.fontSize ?? 14) + 'px',
            textAlign: 'center',
          }}
        />

        <textarea
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          onBlur={() => commit({ description: desc })}
          onKeyDown={stopKeys}
          style={{
            width: '100%',
            border: 'none',
            background: 'transparent',
            color: data.fontColor ?? '#350a2a',
            fontSize: (data.fontSize ?? 14) - 2 + 'px',
            resize: 'none',
            minHeight: '40px',
            textAlign: 'center',
          }}
        />
      </div>

      <Handle
        type="source"
        position={Position.Left}
        id="1"
        className="custom-handle-source"
        style={{ left: '-20%', visibility: selected ? 'visible' : 'hidden' }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="1"
        className="custom-handle-target"
        style={{ left: '-20%', visibility: selected ? 'visible' : 'hidden' }}
      />

      <Handle
        type="source"
        position={Position.Right}
        id="2"
        className="custom-handle-source"
        style={{ right: '-20%', visibility: selected ? 'visible' : 'hidden' }}
      />
      <Handle
        type="target"
        position={Position.Right}
        id="2"
        className="custom-handle-target"
        style={{ right: '-20%', visibility: selected ? 'visible' : 'hidden' }}
      />

      <Handle
        type="source"
        position={Position.Top}
        id="4"
        className="custom-handle-source"
        style={{ top: '-20%', visibility: selected ? 'visible' : 'hidden' }}
      />
      <Handle
        type="target"
        position={Position.Top}
        id="4"
        className="custom-handle-target"
        style={{ top: '-20%', visibility: selected ? 'visible' : 'hidden' }}
      />

      <Handle
        type="source"
        position={Position.Bottom}
        id="3"
        className="custom-handle-source"
        style={{ bottom: '-20%', visibility: selected ? 'visible' : 'hidden' }}
      />
      <Handle
        type="target"
        position={Position.Bottom}
        id="3"
        className="custom-handle-target"
        style={{ bottom: '-20%', visibility: selected ? 'visible' : 'hidden' }}
      />
    </div>
  );
};

export default PreparationNode;
